/* Midnight translation bridge reader (layout/theme.liquid #mw-strings).
   Falls back to the English argument if the bridge is unavailable. Defined
   defensively because several theme scripts share it. */
window.mwString = window.mwString || function (key, fallback) {
  return window.mwT ? window.mwT(key, fallback) : fallback;
};
const MW_AVAILABILITY_FILTER = 'filter.v.availability';
const MW_DEFAULT_COLLECTION_SORT = 'created-descending';

class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 500);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    }
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static collectionRoot() {
    return document.querySelector('[data-mw-canonical-availability]');
  }

  static canonicalAvailability() {
    return FacetFiltersForm.collectionRoot()?.dataset.mwCanonicalAvailability || '';
  }

  static normalizePublicSearchParams(searchParams) {
    const params = new URLSearchParams(searchParams || '');
    if (FacetFiltersForm.canonicalAvailability()) params.delete(MW_AVAILABILITY_FILTER);
    return params.toString();
  }

  static requestSearchParams(searchParams) {
    const params = new URLSearchParams(searchParams || '');
    const root = FacetFiltersForm.collectionRoot();
    const canonicalAvailability = FacetFiltersForm.canonicalAvailability();

    if (canonicalAvailability) {
      params.delete(MW_AVAILABILITY_FILTER);
      params.set(MW_AVAILABILITY_FILTER, canonicalAvailability);
      if (!params.has('sort_by')) {
        params.set('sort_by', root?.dataset.mwDefaultSort || MW_DEFAULT_COLLECTION_SORT);
      }
    }

    return params.toString();
  }

  static setLoading(isLoading) {
    const gridContainer = document.getElementById('ProductGridContainer');
    const collection = gridContainer?.querySelector('.collection');
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');

    if (gridContainer) gridContainer.setAttribute('aria-busy', String(isLoading));
    if (collection) collection.classList.toggle('loading', isLoading);
    if (countContainer) countContainer.classList.toggle('loading', isLoading);
    if (countContainerDesktop) countContainerDesktop.classList.toggle('loading', isLoading);
    FacetFiltersForm.toggleActiveFacets(isLoading);
  }

  static renderLegacyPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    document.getElementById('ProductGridContainer')?.querySelector('.collection')?.classList.add('loading');
    if (countContainer) countContainer.classList.add('loading');
    if (countContainerDesktop) countContainerDesktop.classList.add('loading');

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      if (FacetFiltersForm.filterData.some(filterDataUrl)) {
        FacetFiltersForm.renderLegacySectionFromCache(filterDataUrl, event);
      } else {
        FacetFiltersForm.renderLegacySectionFromFetch(url, event);
      }
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderLegacySectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((html) => {
        FacetFiltersForm.filterData.push({ html, url });
        FacetFiltersForm.renderResponse(html, event);
      });
  }

  static renderLegacySectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderResponse(html, event);
  }
  static renderPage(searchParams, event, updateURLHash = true) {
    if (!FacetFiltersForm.canonicalAvailability()) {
      FacetFiltersForm.renderLegacyPage(searchParams, event, updateURLHash);
      return;
    }

    const publicSearchParams = FacetFiltersForm.normalizePublicSearchParams(searchParams);
    const requestSearchParams = FacetFiltersForm.requestSearchParams(publicSearchParams);
    FacetFiltersForm.searchParamsPrev = publicSearchParams;

    if (FacetFiltersForm.abortController) FacetFiltersForm.abortController.abort();
    FacetFiltersForm.abortController = new AbortController();
    const { signal } = FacetFiltersForm.abortController;
    const requestToken = ++FacetFiltersForm.requestToken;
    const useCache = !FacetFiltersForm.canonicalAvailability();
    const sections = FacetFiltersForm.getSections();

    FacetFiltersForm.setLoading(true);

    const requests = sections.map((section) => {
      const suffix = requestSearchParams ? `&${requestSearchParams}` : '';
      const url = `${window.location.pathname}?section_id=${section.section}${suffix}`;
      const filterDataUrl = (element) => element.url === url;

      if (useCache && FacetFiltersForm.filterData.some(filterDataUrl)) {
        return Promise.resolve(
          FacetFiltersForm.renderSectionFromCache(filterDataUrl, event, requestToken)
        );
      }

      return FacetFiltersForm.renderSectionFromFetch(url, event, requestToken, signal, useCache);
    });

    Promise.all(requests)
      .catch((error) => {
        if (error.name === 'AbortError' || requestToken !== FacetFiltersForm.requestToken) return;
        FacetFiltersForm.renderFailureState();
      })
      .finally(() => {
        if (!signal.aborted && requestToken === FacetFiltersForm.requestToken) {
          FacetFiltersForm.setLoading(false);
        }
      });

    if (updateURLHash) FacetFiltersForm.updateURLHash(publicSearchParams);
  }

  static renderSectionFromFetch(url, event, requestToken, signal, useCache) {
    return fetch(url, {
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
      cache: useCache ? 'default' : 'no-store',
      signal,
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Facet request failed with ${response.status}.`);
        return response.text();
      })
      .then((html) => {
        if (signal.aborted || requestToken !== FacetFiltersForm.requestToken) return;
        if (useCache) FacetFiltersForm.filterData.push({ html, url });
        FacetFiltersForm.renderResponse(html, event);
      });
  }

  static renderSectionFromCache(filterDataUrl, event, requestToken) {
    if (requestToken !== FacetFiltersForm.requestToken) return;
    const html = FacetFiltersForm.filterData.find(filterDataUrl)?.html;
    if (!html) throw new Error('Cached facet response was unavailable.');
    FacetFiltersForm.renderResponse(html, event);
  }

  static renderResponse(html, event) {
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
  }

  static renderFailureState() {
    const gridContainer = document.getElementById('ProductGridContainer');
    if (!gridContainer) return;

    const state = document.createElement('div');
    state.className = 'collection page-width mw-collection-empty-state';
    state.id = 'product-grid';
    state.dataset.id = FacetFiltersForm.sectionId || '';
    state.dataset.mwTotalCount = '0';
    state.dataset.mwCurrentPage = '1';
    state.dataset.mwTotalPages = '0';
    state.setAttribute('role', 'alert');

    const canonicalAvailability = FacetFiltersForm.canonicalAvailability();
    if (canonicalAvailability) state.dataset.mwCanonicalAvailability = canonicalAvailability;

    const message = document.createElement('p');
    message.textContent = mwString('facets.update_failed', 'We couldn\u2019t update the results.');
    const retry = document.createElement('a');
    retry.className = 'underlined-link';
    retry.href = window.location.href;
    retry.textContent = mwString('facets.reload_page', 'Reload the page');
    state.append(message, retry);
    gridContainer.replaceChildren(state);

    const count = document.getElementById('ProductCount');
    const countDesktop = document.getElementById('ProductCountDesktop');
    if (count) count.textContent = mwString('facets.zero_products', '0 products');
    if (countDesktop) countDesktop.textContent = mwString('facets.zero_products', '0 products');
    document.querySelectorAll('[data-mw-toolbar-count]').forEach((node) => {
      node.textContent = mwString('facets.zero_watches', '0 watches');
    });
    document.querySelectorAll('[data-mw-results-cta-count]').forEach((node) => {
      node.textContent = '0';
    });
  }

  static renderProductGridContainer(html) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const source = parsedHTML.getElementById('ProductGridContainer');
    const target = document.getElementById('ProductGridContainer');
    if (!source || !target) throw new Error('Facet response did not include a product grid.');
    target.innerHTML = source.innerHTML;
  }

  static renderProductCount(html) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const source = parsedHTML.getElementById('ProductCount');
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    if (!source || !container) throw new Error('Facet response did not include a product count.');
    container.innerHTML = source.innerHTML;
    if (containerDesktop) containerDesktop.innerHTML = source.innerHTML;
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');

    const facetDetailsElements =
      parsedHTML.querySelectorAll('#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter');
    const matchesIndex = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.dataset.index === jsFilter.dataset.index : false;
    }
    const facetsToRender = Array.from(facetDetailsElements).filter(element => !matchesIndex(element));
    const countsToRender = Array.from(facetDetailsElements).find(matchesIndex);

    facetsToRender.forEach((element) => {
      const target = document.querySelector(`.js-filter[data-index="${element.dataset.index}"]`);
      if (target) target.innerHTML = element.innerHTML;
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    })

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting', '.mw-collection-quickfilters'];

    mobileElementSelectors.forEach((selector) => {
      const source = html.querySelector(selector);
      const target = document.querySelector(selector);
      if (!source || !target) return;
      target.innerHTML = source.innerHTML;
    });

    const mobileForm = document.getElementById('FacetFiltersFormMobile');
    const menuDrawer = mobileForm?.closest('menu-drawer');
    if (menuDrawer && typeof menuDrawer.bindEvents === 'function') menuDrawer.bindEvents();
  }

  static renderCounts(source, target) {
    const targetElement = target.querySelector('.facets__selected');
    const sourceElement = source.querySelector('.facets__selected');

    const targetElementAccessibility = target.querySelector('.facets__summary');
    const sourceElementAccessibility = source.querySelector('.facets__summary');

    if (sourceElement && targetElement) {
      target.querySelector('.facets__selected').outerHTML = source.querySelector('.facets__selected').outerHTML;
    }

    if (targetElementAccessibility && sourceElementAccessibility) {
      target.querySelector('.facets__summary').outerHTML = source.querySelector('.facets__summary').outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    const sectionId = document.getElementById('product-grid')?.dataset.id || FacetFiltersForm.sectionId;
    if (!sectionId) throw new Error('Collection section id was unavailable.');
    FacetFiltersForm.sectionId = sectionId;
    return [{ section: sectionId }];
  }

  static initializeCanonicalCollection() {
    if (!FacetFiltersForm.canonicalAvailability()) return;

    const initial = FacetFiltersForm.normalizePublicSearchParams(window.location.search.slice(1));
    FacetFiltersForm.searchParamsInitial = initial;
    FacetFiltersForm.searchParamsPrev = initial;

    const current = window.location.search.slice(1);
    if (initial !== current) {
      history.replaceState(
        { searchParams: initial },
        '',
        `${window.location.pathname}${initial ? `?${initial}` : ''}${window.location.hash}`
      );
    }

    FacetFiltersForm.renderPage(initial, null, false);
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'))
      this.onSubmitForm(searchParams, event)
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            const noJsElements = document.querySelectorAll('.no-js-list');
            noJsElements.forEach((el) => el.remove());
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event)
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url = event.currentTarget.href.indexOf('?') == -1 ? '' : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.abortController = null;
FacetFiltersForm.requestToken = 0;
FacetFiltersForm.sectionId = document.getElementById('product-grid')?.dataset.id || '';
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();
FacetFiltersForm.initializeCanonicalCollection();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input')
      .forEach(element => element.addEventListener('change', this.onRangeChange.bind(this)));
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('min', 0);
    if (maxInput.value === '') minInput.setAttribute('max', maxInput.getAttribute('max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('min'));
    const max = Number(input.getAttribute('max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);
